import {
  ClipboardCopyIcon,
  DownloadIcon,
  PlayIcon,
} from '@radix-ui/react-icons';
import { Badge, Flex, IconButton, Spinner, Text } from '@radix-ui/themes';
import { FileHelper } from 'classes/helpers/file.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { FlexTableWrapper } from 'components/common/layout/flex-table-wrapper';
import { CommonTableHoC } from 'components/common/table';
import { getFireVideoID } from 'components/common/video/annotated-player';
import { formatDate } from 'components/sections/admin-portal/machines/dialogs/settings/machine-errors/common';
import { getPitch } from 'components/sections/admin-portal/machines/dialogs/settings/machine-shot-history/common';
import { MachineShotConfidenceBadge } from 'components/sections/admin-portal/machines/dialogs/settings/machine-shot-history/machine-shot-confidence-badge';
import { MachineShotHistoryToolbar } from 'components/sections/admin-portal/machines/dialogs/settings/machine-shot-history/machine-shot-history-toolbar';
import { MachineContext } from 'contexts/machine.context';
import { ACTIONS_KEY } from 'enums/tables';
import { IMenuAction } from 'interfaces/i-menus';
import { ITableCheckable } from 'interfaces/tables/checking';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ITableInfiniteLoader } from 'interfaces/tables/infinite-loader';
import { ITableSelectable } from 'interfaces/tables/selection';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { HitHelper } from 'lib_ts/classes/hit.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { DictOf } from 'lib_ts/interfaces/common/i-dictionary';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { IPitch } from 'lib_ts/interfaces/pitches';
import { IMachineShot } from 'lib_ts/interfaces/training/i-machine-shot';
import { compact, first } from 'lodash';
import { useContext, useMemo, useState } from 'react';
import { MainService } from 'services/main.service';
import { StereoVideosService } from 'services/stereo-videos.service';

interface IProps {
  hasMore: boolean;
  shots: IMachineShot[];
  newShotID: string;
  updatedShotID: string;
  pitchDict: DictOf<IPitch>;
  hitterDict: DictOf<{ name: string }>;
  hitterOptions: IOption[];
  canFilterByHitter: boolean;
  loading: boolean;
  onLoadMore: () => void;
  onSelect: (shot: IMachineShot) => void;
}

export const MachineShotHistoryTable = (props: IProps) => {
  const { machine } = useContext(MachineContext);
  const {
    shots,
    loading,
    hasMore,
    pitchDict,
    hitterDict,
    onSelect,
    onLoadMore,
  } = props;

  const [downloadingVideos, setDownloadingVideos] = useState(false);

  const columns = useMemo<ITypedTableColumn<IMachineShot>[]>(
    () => [
      {
        label: 'Date',
        key: '_created',
        formatFn: (s) => <Text size="1">{formatDate(s._created)}</Text>,
      },
      {
        label: 'Pitch Name',
        key: 'pitchName',
        formatFn: (s) => (
          <Badge
            color={s.pitch_id ? RADIX.COLOR.ACCENT : RADIX.COLOR.SECONDARY}
          >
            {getPitch(pitchDict, s.pitch_id).name || 'N/A'}
          </Badge>
        ),
      },
      {
        label: 'Hitter Name',
        key: 'hitterName',
        formatFn: (s) => (
          <Text>
            {StringHelper.shortenName(
              hitterDict[s.fire_id as string]?.name ?? '-'
            )}
          </Text>
        ),
      },
      {
        label: 'Hit Result',
        key: 'hit_result',
        formatFn: (s) => (
          <Text>
            {s.hit_outcome ? HitHelper.getOutcomeLabel(s.hit_outcome) : '-'}
          </Text>
        ),
      },
      {
        label: 'Mode',
        key: 'mode',
        formatFn: (s) => <Text>{s.training ? 'Training' : 'BP'}</Text>,
      },
      {
        label: 'Confidence',
        key: 'confidence',
        formatFn: (s) => (
          <MachineShotConfidenceBadge
            shot={s}
            pitch={getPitch(pitchDict, s.pitch_id)}
          />
        ),
      },
      {
        label: '',
        key: ACTIONS_KEY,
        formatFn: (s) => {
          const videoID = getFireVideoID({
            annotated_video_id: s.annotated_video_id,
            replay_video_id: s.replay_video_id,
          });

          return (
            <IconButton variant="soft" size="1" disabled={!videoID}>
              <PlayIcon />
            </IconButton>
          );
        },
        disableSort: true,
      },
    ],
    [pitchDict, hitterDict]
  );

  const tableSelection: ITableSelectable = {
    enableSelect: true,
    afterChangeSelected: onSelect,
  };

  const downloadVideos = async (videoIDs: string[]) => {
    setDownloadingVideos(true);

    const videos = await StereoVideosService.getInstance().getMany(videoIDs);

    const urlDict = await MainService.getInstance().signUrls(
      videos.map((m) => ({
        region: m.region,
        path: m.path,
      }))
    );

    if (videos.length === 1) {
      const video = first(videos);
      if (!video) {
        // shouldn't trigger
        return;
      }

      const entry = urlDict[video.path];
      if (!entry) {
        // shouldn't trigger
        return;
      }

      window.open(entry.url, '_blank');
      setDownloadingVideos(false);
      return;
    }

    // zip multiple videos (without compression) into a single download file
    const files: { name: string; url: string }[] = [];

    for (const video of videos) {
      const entry = urlDict[video.path];

      if (!entry) {
        continue;
      }

      files.push({
        name: video._id,
        url: entry.url,
      });
    }

    if (files.length === 0) {
      return;
    }

    await FileHelper.zipFiles({
      fileName: `${machine.machineID} Videos`,
      files,
    });

    setDownloadingVideos(false);
  };

  const getCheckedMenuActions = (checked: IMachineShot[]): IMenuAction[] => {
    const videoIDs = compact(
      ArrayHelper.unique(
        checked.map((s) =>
          getFireVideoID({
            annotated_video_id: s.annotated_video_id,
            replay_video_id: s.replay_video_id,
          })
        )
      )
    );

    const numVideos = videoIDs.length;

    return [
      {
        label:
          numVideos > 1 ? `Download ${numVideos} videos` : 'Download video',
        invisible: !numVideos,
        prefixIcon: <DownloadIcon />,
        disabled: downloadingVideos || !numVideos,
        onClick: () => downloadVideos(videoIDs),
      },
      {
        label: 'Copy shot IDs',
        prefixIcon: <ClipboardCopyIcon />,
        onClick: () => {
          const ids = checked.map((s) => s._id).join(', ');
          navigator.clipboard.writeText(ids);

          const message_md =
            checked.length > 1
              ? `Copied ${checked.length} shot IDs to clipboard`
              : `Copied ID ${ids} to clipboard`;

          NotifyHelper.success({
            message_md,
          });
        },
      },
    ];
  };

  const checkable: ITableCheckable = {
    checkboxColumnIndex: 0,
    checkedActions: getCheckedMenuActions,
  };

  const tableInfiniteScrollable: ITableInfiniteLoader = {
    onLoadMore,
    hasMore,
    rootMargin: '128px',
  };

  return (
    <Flex
      direction="column"
      gap={RADIX.FLEX.GAP.MD}
      className={RADIX.VFLEX.GROW}
    >
      <FlexTableWrapper
        gap={RADIX.FLEX.GAP.FORM}
        header={
          <MachineShotHistoryToolbar
            hitterOptions={props.hitterOptions}
            canFilterByHitter={props.canFilterByHitter}
          />
        }
        table={
          <CommonTableHoC
            dataTestID="MachineShotHistory"
            identifier="MachineShotHistoryTable"
            displayColumns={columns}
            displayData={shots}
            loading={loading}
            footerRightSlot={downloadingVideos ? <Spinner /> : undefined}
            rowClassNameFn={(s: IMachineShot) => {
              if (!s) {
                return '';
              }

              if (s._id === props.updatedShotID) {
                return 'table-updated-row';
              }

              if (s._id === props.newShotID) {
                return 'table-new-row';
              }

              return '';
            }}
            {...tableSelection}
            {...checkable}
            {...tableInfiniteScrollable}
            vFlex
            skipResetSelected
          />
        }
      />
    </Flex>
  );
};
